import React, { useMemo } from 'react'

import {
  BodyText,
  CheckBoxInput,
  FieldController,
  HeadingSmall,
  Label,
  LinkModal,
} from '@pb/components'
import { getLabelFromStorage } from '@pb/utils'
import cn from 'classnames'
import { path } from 'ramda'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { FieldList } from 'ui/Form'

import { useUpgradeToClubFieldsConfig } from '../../hooks/useUpgradeToClubFieldsConfig'
import styles from './UpgradeToClub.scss'

const UpgradeToClubFormContent: React.FC<any> = ({ shouldHideOptionalFields }) => {
  const { errors } = useFormContext()

  const marketMinAge = useSelector(appSelectors.getMinAge)
  const isClubMarket = useSelector(appSelectors.getIsClubMarket)
  const shouldShowExtraConsents = useSelector(appSelectors.getShouldShowExtraConsents)
  const upgradeToClubFields = useUpgradeToClubFieldsConfig()

  const visibleUpgradeToClubFields = useMemo(() => {
    if (shouldHideOptionalFields) {
      return upgradeToClubFields.filter((field) => field.required)
    }

    return upgradeToClubFields
  }, [shouldHideOptionalFields, upgradeToClubFields])

  const clubTermsAndConditionURL = useSelector(appSelectors.getClubTermsAndConditionURL)
  const privacyPolicyURL = useSelector(appSelectors.getPrivacyPolicyURL)
  const dataProcessingConsentLink = useSelector(appSelectors.getDataProcessingConsentLink)
  const overseasTransferConsentLink = useSelector(appSelectors.getOverseasTransferConsentLink)
  const thirdPartyConsentLink = useSelector(appSelectors.getThirdPartyConsentLink)
  const marketingInformationConsentLink = useSelector(
    appSelectors.getMarketingInformationConsentLink,
  )
  const checkboxesTitle = getLabelFromStorage('register.acceptlegal.aggrement')
  const viewMoreLabel = getLabelFromStorage('text.view.more')
  const footerClubMarketPrivacyConditionLabel = getLabelFromStorage(
    'footer.text.club.market.privacy.condition1',
  )
  const footerClubMarketInfoLabel = getLabelFromStorage('footer.text.club.market.info.tc')
  const requiredCheckboxLabel = getLabelFromStorage('form.required')

  const extraConsentCheckboxes = [
    {
      id: 'termsAndCondition2',
      labelCopyKey: 'register.termsAndCondition2Text.hmConditions',
      viewMoreEndpoint: clubTermsAndConditionURL,
      required: true,
    },
    {
      id: 'collectionAndStorage',
      labelCopyKey: 'register.collectionAndStorage.hmConditions',
      viewMoreEndpoint: dataProcessingConsentLink,
      required: true,
    },
    {
      id: 'overseasTransfer',
      labelCopyKey: 'register.overseasTransfer.hmConditions',
      viewMoreEndpoint: overseasTransferConsentLink,
      required: true,
    },
    {
      id: 'thirdPartyTransfer',
      labelCopyKey: 'register.thirdPartyTransfer.hmConditions',
      viewMoreEndpoint: thirdPartyConsentLink,
      required: true,
    },
    {
      id: 'hmNewsSubscription',
      labelCopyKey: 'register.marketingCommunication.hmConditions',
      viewMoreEndpoint: marketingInformationConsentLink,
      required: false,
    },
  ]

  return (
    <>
      <FieldList fields={visibleUpgradeToClubFields} />
      {isClubMarket && shouldShowExtraConsents ? (
        <>
          <HeadingSmall className={styles.checkboxesTitle}>{checkboxesTitle}</HeadingSmall>
          {extraConsentCheckboxes.map((box, boxIndex) => (
            <div
              className={cn(styles.checkboxWrapper, {
                [styles.lastCheckbox]: boxIndex === extraConsentCheckboxes.length - 1,
              })}
              key={box.id}
            >
              <FieldController
                render={({ value, ...rest }) => (
                  <CheckBoxInput
                    checked={value}
                    {...rest}
                    label={
                      <BodyText Tag="span">
                        <Label copyKey={box.labelCopyKey} />
                      </BodyText>
                    }
                    id={box.id}
                    name={box.id}
                    className={styles.checkbox}
                    shouldShowValidationIcon={false}
                    error={path([box.id, 'message'], errors)}
                    required={box.required}
                    supportingContent={
                      box.viewMoreEndpoint && (
                        <LinkModal
                          label={viewMoreLabel}
                          endpoint={box.viewMoreEndpoint}
                          withReplacements={false}
                          shouldForceDisplay
                          isDisableLoader
                        />
                      )
                    }
                  />
                )}
                name={box.id}
                defaultValue={false}
                rules={box.required && { required: requiredCheckboxLabel }}
              />
            </div>
          ))}
        </>
      ) : (
        <>
          {!shouldHideOptionalFields && (
            <div className={cn(styles.checkboxWrapper, styles.lastCheckbox)}>
              <FieldController
                render={({ value, ...rest }) => (
                  <CheckBoxInput
                    checked={value}
                    verticalAlign="top"
                    {...rest}
                    label={
                      <>
                        <BodyText Tag="p">
                          <Label copyKey="register.club.hmConditions" replacements={marketMinAge} />
                        </BodyText>
                        <BodyText Tag="p">
                          <Label copyKey="account.gdpr.newsletter.subscription" />
                        </BodyText>
                      </>
                    }
                    id="hmNewsSubscription"
                    name="hmNewsSubscription"
                    className={styles.checkbox}
                    shouldShowValidationIcon={false}
                  />
                )}
                name="hmNewsSubscription"
                defaultValue={false}
              />
            </div>
          )}

          <LinkModal
            label={footerClubMarketInfoLabel}
            endpoint={clubTermsAndConditionURL}
            className={styles.finePrint}
            shouldForceDisplay
            isDisableLoader
          />
        </>
      )}
      <LinkModal
        label={footerClubMarketPrivacyConditionLabel}
        endpoint={privacyPolicyURL}
        className={styles.finePrint}
        shouldForceDisplay
        isDisableLoader
      />
    </>
  )
}

export default UpgradeToClubFormContent
